import { Link } from "@tanstack/react-router";
import { Copy, EllipsisVertical, ExternalLink, MessageSquareText } from "lucide-react";

import type { DirectoryItem } from "@/api/db/people";
import { ManualStatusIcon } from "@/components/people/manual-status-icon";
import { SourceBadges } from "@/components/people/source-badge";
import { usePopover } from "@/hooks/use-popover";
import { FunnelLabels } from "@/lib/labels";
import { format } from "@/lib/format";
import { PessoasLabels } from "@/lib/labels/pessoas";
import { cn } from "@/lib/utils";

import type { PessoasSearch } from "../index";

type LinkKind = DirectoryItem["link"];

const MONOGRAM_TINT: Record<LinkKind, string> = {
	patient_confirmed: "bg-primary-soft text-primary",
	patient_potential: "bg-warning-soft text-warning",
	only_conversation: "bg-secondary text-muted-foreground",
};

const SITU_DOT: Record<LinkKind, string> = {
	patient_confirmed: "bg-primary",
	patient_potential: "bg-warning",
	only_conversation: "bg-muted-foreground",
};

function displayName(person: DirectoryItem) {
	const readable = format.readableName(person.name);

	if (readable !== null) {
		return readable;
	}

	return format.phone(person.phone) ?? PessoasLabels.unnamed;
}

function monogram(person: DirectoryItem) {
	const readable = format.readableName(person.name);

	if (readable === null) {
		return "?";
	}

	const parts = readable.split(/\s+/u).filter(Boolean);
	const initials = parts.slice(0, 2).map((part) => part[0]);

	return initials.join("").toUpperCase();
}

function subline(person: DirectoryItem) {
	if (person.conversation_stage) {
		return FunnelLabels.stage(person.conversation_stage);
	}

	if (format.readableName(person.name) === null) {
		return PessoasLabels.unnamedContact;
	}

	return null;
}

export function PessoasCards({ items, search }: { items: DirectoryItem[]; search: PessoasSearch }) {
	return (
		<div className="min-w-0">
			<div
				aria-hidden="true"
				className="directory-columns hidden border-b border-border bg-background/60 px-5 py-3 text-xs font-medium text-muted-foreground xl:grid"
			>
				<span>Pessoa</span>
				<span>Vínculo</span>
				<span>Contato</span>
				<span>Fontes</span>
				<span />
			</div>
			<ul data-component="pessoas-cards" className="min-w-0 divide-y divide-border">
				{items.map((person) => (
					<PessoasCard key={person.id} person={person} search={search} />
				))}
			</ul>
		</div>
	);
}

function Monogram({ person }: { person: DirectoryItem }) {
	return (
		<span
			aria-hidden="true"
			className={cn(
				"flex size-10 shrink-0 items-center justify-center rounded-md text-xs font-semibold",
				MONOGRAM_TINT[person.link],
			)}
		>
			{monogram(person)}
		</span>
	);
}

function NameBlock({ person, search }: { person: DirectoryItem; search: PessoasSearch }) {
	const sub = subline(person);

	return (
		<div className="min-w-0">
			<Link
				to="/pessoa/$id"
				params={{ id: person.id }}
				search={{ q: search.q, source: search.source, page: search.page }}
				className="block min-w-0 break-words text-sm font-semibold text-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
			>
				{displayName(person)}
			</Link>

			{(!!sub || !!person.manual_status) && (
				<div className="mt-0.5 flex min-w-0 items-center gap-2">
					{!!sub && <span className="truncate text-xs text-muted-foreground">{sub}</span>}
					<ManualStatusIcon status={person.manual_status} />
				</div>
			)}
		</div>
	);
}

function Situacao({ person }: { person: DirectoryItem }) {
	return (
		<span
			data-slot="link"
			data-link={person.link}
			className="inline-flex items-center gap-2 text-sm text-foreground"
		>
			<span className={cn("size-1.5 rounded-full", SITU_DOT[person.link])} />
			{PessoasLabels.link[person.link]}
		</span>
	);
}

function ProblemFlag({ person }: { person: DirectoryItem }) {
	if (!person.has_problem) {
		return null;
	}

	return (
		<span
			data-slot="problem"
			className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive"
		>
			<span className="size-1.5 rounded-full bg-destructive" />
			{PessoasLabels.problem}
		</span>
	);
}

function CardMenu({ person, search }: { person: DirectoryItem; search: PessoasSearch }) {
	const { open, setOpen, rootRef, panelRef } = usePopover<HTMLSpanElement>("right");

	const phone = format.phone(person.phone);

	function copyPhone() {
		if (person.phone !== null) {
			void navigator.clipboard.writeText(person.phone);
		}

		setOpen(false);
	}

	return (
		<span ref={rootRef} className="relative ml-auto inline-flex">
			<button
				type="button"
				data-slot="row-menu"
				aria-expanded={open}
				aria-label="Ações"
				onClick={() => setOpen((value) => !value)}
				className={cn(
					"control-button button-ghost size-11 md:size-9",
					"hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
					open && "bg-secondary text-foreground",
				)}
			>
				<EllipsisVertical className="size-4" />
			</button>

			{open && (
				<div ref={panelRef} popover="manual" className="anchored-popover min-w-44 p-1">
					<p className="truncate px-2 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">
						{displayName(person)}
					</p>

					<Link
						to="/pessoa/$id"
						params={{ id: person.id }}
						search={{ q: search.q, source: search.source, page: search.page }}
						className="menu-item"
					>
						<ExternalLink className="size-3.5 text-muted-foreground" />
						Abrir ficha
					</Link>

					{person.sendpulse_id !== null && (
						<Link
							to="/pessoa/$id"
							params={{ id: person.id }}
							search={{ tab: "conversa" }}
							className="menu-item"
						>
							<MessageSquareText className="size-3.5 text-muted-foreground" />
							Abrir conversa
						</Link>
					)}

					{phone !== null && (
						<button type="button" onClick={copyPhone} className="menu-item">
							<Copy className="size-3.5 text-muted-foreground" />
							Copiar telefone
						</button>
					)}
				</div>
			)}
		</span>
	);
}

function PessoasCard({ person, search }: { person: DirectoryItem; search: PessoasSearch }) {
	const phone = format.phone(person.phone);

	return (
		<li
			data-component="pessoas-card"
			data-name={person.name_norm}
			data-id={person.id}
			data-problem={person.has_problem}
			className="directory-columns relative px-4 py-4 transition-colors hover:bg-background/70 sm:px-5"
		>
			<div className="flex min-w-0 items-center gap-3 pr-10 xl:pr-0">
				<Monogram person={person} />
				<NameBlock person={person} search={search} />
			</div>
			<div className="flex min-w-0 flex-wrap items-center gap-2 xl:flex-col xl:items-start">
				<Situacao person={person} />
				<ProblemFlag person={person} />
			</div>
			<div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 xl:flex-col xl:items-start">
				{phone !== null && <span className="font-mono text-xs text-foreground">{phone}</span>}
				{!!person.city && <span className="text-xs text-muted-foreground">{person.city}</span>}
			</div>
			<SourceBadges sources={person.sources} className="px-1.5 py-0.5" />
			<span className="absolute right-4 top-3 inline-flex xl:static">
				<CardMenu person={person} search={search} />
			</span>
		</li>
	);
}
